import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import loginImage from '../../assets/images/login-image.png';
import logo from '../../assets/images/logo-1.svg';
import ReactLoading from 'react-loading';
import { ValidateOtpAsync } from '../../API/Account/ValidateOtpAsync';
import { GenerateOtpAsync } from '../../API/Account/GenerateOtpAsync';
import { getUserProfile } from '../../API/UserProfileApi/GetUserProfile';
import { useLocalization } from "../Context/MultiLanguageContext";

interface RegisterProps {
  updateUserName: (newUserName: string) => void;
}

const Register: React.FC<RegisterProps> = ({ updateUserName }) => {
  const { t } = useLocalization();

  const [formData, setFormData] = useState({
    FullName: '',
    email: '',
    PhoneNumber: '',
    CountryCode: '+91',
    Otp: '',
    ReferralId: '',
    ReferralName: ''// optional, max 6 digits
  });

  const [otp, setOtp] = useState(Array(6).fill(''));
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [otpError, setOtpError] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [cooldown, setCooldown] = useState(0);
  const [isOtpValid, setIsOtpValid] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [errors, setErrors] = useState({
    FullName: '',
    PhoneNumber: '',
    ReferralId: '',
  });

  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [modalType, setModalType] = useState<"privacy" | "terms" | null>(null); const navigate = useNavigate();
  const location = useLocation();

  const { email, isRegisteredUser, autoFillOtp } = location.state || {};

  const [originalReferral, setOriginalReferral] = useState({
    id: '',
    name: ''
  });

  useEffect(() => {
    if (email) {
      setFormData((prevData) => ({ ...prevData, email }));
    }
    if (autoFillOtp && autoFillOtp.length === 6) {
      setOtp(autoFillOtp.split(""));
      setIsOtpValid(true);
    }
    // read referral id from cookie (if any)
    const getCookie = (name: string) => {
      const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : null;
    };
    const cookieRef = getCookie('referralId');
    if (cookieRef && /^\\d{1,6}$/.test(cookieRef)) {
      setFormData((prev) => ({ ...prev, ReferralId: cookieRef }));
    }
  }, [email, autoFillOtp]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === "CountryCode") {
      if (!/^\+?\d{0,3}$/.test(value)) {
        return;
      }
    }

    if (name === "ReferralId") {
      if (!/^\d{0,6}$/.test(value)) {
        return;
      }

      setFormData((prev) => {
        if (value === originalReferral.id) {
          return {
            ...prev,
            ReferralId: value,
            ReferralName: originalReferral.name
          };
        }

        return {
          ...prev,
          ReferralId: value,
          ReferralName: ''
        };
      });

      return;
    }

    if (name === "PhoneNumber" && !isRegisteredUser) {
      if (!/^\d+$/.test(value) && value !== "") {
        setErrors((prevErrors) => ({
          ...prevErrors,
          PhoneNumber: t("phoneOnlyDigits"),
        }));
      } else {
        setErrors((prevErrors) => ({ ...prevErrors, PhoneNumber: "" }));
      }
    }

    // ✅ THIS WAS MISSING
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };


  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const value = e.target.value;
    setIsOtpValid(false);
    if (/^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      const isComplete = newOtp.every(digit => digit.length === 1);
      setIsOtpValid(isComplete);
      if (value && index < otp.length - 1) {
        otpRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };


  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);

    if (!isRegisteredUser && !agreedToTerms) {
      if (!toast.isActive("terms-error")) {
        toast.error(t("agreeTerms"), { toastId: "terms-error" });
      }
      return;
    }

    if (otp.join('').length !== 6) {
      if (!toast.isActive("otp-missing")) {
        toast.error(t("enterOtpError"), { toastId: "otp-missing" });
      }
      setOtpError(true);
      return;
    }
    if (!isRegisteredUser && (!formData.FullName || !formData.PhoneNumber || !formData.email)) {
      if (!toast.isActive("form-error")) {
        toast.error(t("fillAllFields"), { toastId: "form-error" });
      }
      setOtpError(true);
      return;
    }

    if (!isRegisteredUser) {
      const countryCode = formData.CountryCode.replace("+", "");
      const phone = formData.PhoneNumber;

      if (!/^\d{1,3}$/.test(countryCode)) {
        setErrors((prev) => ({
          ...prev,
          PhoneNumber: "Invalid country code",
        }));
        return;
      }

      if ((countryCode + phone).length > 15) {
        setErrors((prev) => ({
          ...prev,
          PhoneNumber: "Phone number cannot exceed 15 digits",
        }));
        return;
      }

      setErrors((prev) => ({
        ...prev,
        PhoneNumber: "",
      }));
    }

    if (!navigator.onLine) {
      if (!toast.isActive("offline-error")) {
        toast.error(t("internetConnection"), { toastId: "offline-error" });
      }
      return;
    }

    setLoading(true);
    setOtpError(false);
    setAttempts(attempts + 1);

    const FullNo = formData.PhoneNumber ? formData.CountryCode + formData.PhoneNumber : null;



    try {
      const storedRef = (() => {
        try {
          return localStorage.getItem('referralId') || sessionStorage.getItem('referralId') || null;
        } catch (err) {
          return null;
        }
      })();
      // prefer the value user entered (formData) otherwise fallback to storage
      const referalIdStored = formData.ReferralId
        ? Number(formData.ReferralId)
        : storedRef
          ? Number(storedRef)
          : undefined;

      const responseData = await ValidateOtpAsync(
        formData.email,
        isRegisteredUser ? undefined : formData.FullName,
        isRegisteredUser ? undefined : (FullNo ?? undefined),
        otp.join(''),
        referalIdStored
      );


      if (!responseData) {
        if (navigator.onLine && !toast.isActive("otp-error")) {
          toast.error(t("Invalidotp"), { toastId: "otp-error" });
        }
        setOtp(["", "", "", "", "", ""]);
        setOtpError(true);
        return;
      }

      const { token, refreshToken } = responseData;
      if (token) {
        // store both tokens under consistent keys
        localStorage.setItem("token", token);
        if (refreshToken) {
          localStorage.setItem("refreshToken", refreshToken);
        }

        document.cookie = "referralId=; path=/; domain=.getmethere.in; max-age=0";
        document.cookie = "referralName=; path=/; domain=.getmethere.in; max-age=0";

        document.cookie = "referralId=; path=/; max-age=0";
        document.cookie = "referralName=; path=/; max-age=0";

        // now fetch profile with fetchWithToken
        const profileData = await getUserProfile(t);
        const userName = profileData.userName;

        if (userName) {
          localStorage.setItem("userName", userName);
          updateUserName(userName);
          // toast.success(t("Validotp"));

          // Check for stored URL to redirect to
          const storedUrl = localStorage.getItem('redirectUrl');
          setTimeout(() => {
            if (storedUrl) {
              localStorage.removeItem('redirectUrl');
              navigate(storedUrl, { replace: true });
            } else {
              navigate('/home', { replace: true });
            }
          }, 1000);
        } else {
          toast.error(t("noProfileData"));
        }
      } else {
        toast.error(t("Invalidotp"), { toastId: "otp-error" });
        setOtp(Array(6).fill(''));
        setOtpError(true);
      }
    } catch (error) {
      console.error("Error during OTP validation:", error);
      toast.error(t("Invalidotp"), { toastId: "otp-error" });
      setOtp(Array(6).fill(''));
      setOtpError(true);
    } finally {
      setLoading(false);
    }
  };


  const handleResendOtp = async () => {
    if (!formData.email) {
      toast.error(t("validEmail"));
      return;
    }

    setAttempts(0);
    setLoading(true);

    try {

      const responseData = await GenerateOtpAsync(formData.email, t);
      if (responseData) {
        setCooldown(30);
        setTimeout(() => {
          navigate('/register', {
            state: {
              email: formData.email,
              otpSent: true,
              isRegisteredUser: responseData.isRegisteredUser,
            },
          });
        }, 1500);
      } else {
        toast.error(t("generateOtpFailed"));
      }
    } catch (error) {
      toast.error(t("generateOtpFailed2"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (cooldown > 0) {
      const timer = setInterval(() => {
        setCooldown((prevCooldown) => prevCooldown - 1);
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [cooldown]);

  useEffect(() => {
    const getCookie = (name: string) => {
      const match = document.cookie.match(
        new RegExp('(?:^|; )' + name + '=([^;]*)')
      );
      return match ? decodeURIComponent(match[1]) : null;
    };

    const cookieRefId = getCookie("referralId");
    const cookieRefName = getCookie("referralName");

    if (cookieRefId && /^\d{1,6}$/.test(cookieRefId)) {
      setFormData((prev) => ({
        ...prev,
        ReferralId: cookieRefId,
        ReferralName: cookieRefName || ''
      }));

      // 👇 store original
      setOriginalReferral({
        id: cookieRefId,
        name: cookieRefName || ''
      });
    }
  }, []);

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSubmit(e);
  };

  const openModal = (type: "privacy" | "terms") => {
    setModalType(type);
  };

  const closeModal = () => {
    setModalType(null);
  };



  const PrivacyPolicyContent = () => (
    <div className="max-h-96 overflow-y-auto text-sm text-gray-700">
      <h1 className="text-2xl font-bold text-center">{t("getmethere")}</h1>
      <p className="mt-4">
        {t("privacyIntro")}
      </p>
      <h2 className="text-xl font-bold mt-6"> {t("privacyPolicy")}</h2>
      <p className="text-sm"> {t("privacyPolicyLastUpdated")}</p>
      <p className="mt-4">
        <strong>{t("getmethere")}</strong> {t("privacyDescription1")}
      </p>
      <p className="mt-4">
        {t("privacyDescription2")} <strong>{t("getmethere")}</strong> {t("privacyDescription3")} <strong>{t("getmethere")}</strong>{t("privacyDescription4")}
      </p>
      <h3 className="text-lg font-bold mt-6">{t("personalInfoHeading")}</h3>
      <ul className="list-disc ml-6 mt-2">
        <li>{t("personalInfo1")}</li>
        <li><strong>{t("personalInfo2Title")}</strong> {t("personalInfo2Body")}</li>
        <li><strong>{t("personalInfo3Title")}</strong> {t("personalInfo3Body")}</li>
        <li><strong>{t("personalInfo4Title")}</strong> {t("personalInfo4Body")}</li>
        <li><strong>{t("personalInfo5Title")}</strong> {t("personalInfo5Body")}</li>
        <li><strong>{t("personalInfo6Title")}</strong> {t("personalInfo6Body")}</li>
      </ul>

      <p className="mt-4">{t("autoInfoCollection")}</p>

      <h3 className="text-lg font-bold mt-6">{t("identityVerificationHeading")}</h3>
      <p className="mt-2">{t("identityVerificationContent")}</p>
    </div>
  );


  const TermsOfServiceContent = () => (
    <>
      <h1 className="text-2xl font-bold text-center">{t("getmethere")}</h1>

      <p className="mt-4">
        {t("terms1")}
      </p>

      <p className="mt-4">
        <strong>{t("getmethere")}</strong> {t("terms2")}
      </p>

      <p className="mt-4">
        {t("complaintDisclosure")}
      </p>

      <h3 className="text-lg font-bold mt-6">{t("dataSharingHeading")}</h3>
      <ul className="list-disc ml-6 mt-2">
        <li>
          {t("dataSharingInfo")}
        </li>
      </ul>

      <h3 className="text-lg font-bold mt-6">{t("disclosureHeading")}</h3>
      <ul className="list-disc ml-6 mt-2">
        <li>
          {t("disclosureInfo")}
        </li>
      </ul>

      <h3 className="text-lg font-bold mt-6">{t("exitPolicyHeading")}</h3>
      <p className="mt-2">
        {t("exitPolicyInfo")}
      </p>
    </>
  );

  return (
    <div className="content-wrapper select-none overflow-hidden">
      <main>
        <div className="mx-auto max-w-screen-2xl h-screen flex items-center p-4 md:p-6 2xl:p-10 overflow-hidden">
          <div className="flex flex-wrap items-center">
            <div className="hidden w-full h-full lg:block lg:w-3/5">
              <div className="px-26 py-0 text-center -mt-5">
                <img
                  className="h-full w-full object-cover object-top"
                  src={loginImage}
                  alt="illustration"
                />
              </div>
            </div>

            <div className="w-full lg:w-2/5">
              <div className="w-full lg:w-3/4 p-4 sm:p-12.5 xl:p-17.5 m-auto">
                <a className="mb-4 inline-block" href="">
                  <img src={logo} alt="Logo" className="h-10" />
                </a>
                <h1 className="mb-8 text-3xl font-bold text-gray-800 tracking-tight">
                  {isRegisteredUser ? t("login") : t("verifyAccount")}
                </h1>


                <form onSubmit={handleFormSubmit}>
                  {!isRegisteredUser && (
                    <>
                      <div>
                        <input
                          type="text"
                          name="FullName"
                          placeholder={t("enterYourName")}
                          value={formData.FullName}
                          onChange={handleChange}
                          maxLength={32}
                          className="w-full rounded-lg bg-slate-50 py-3 pl-6 pr-10 outline-none focus:border-blue focus-visible:shadow-none"
                          required={!isRegisteredUser}
                        />
                        <div className="min-h-[20px] mt-1">
                          {errors.FullName && <span className="text-red-500 text-xs">{errors.FullName}</span>}
                        </div>
                      </div>

                      <div>
                        <div className="flex gap-2">

                          <input
                            type="text"
                            name="CountryCode"
                            value={formData.CountryCode}
                            maxLength={4}
                            onChange={handleChange}
                            required={!isRegisteredUser}
                            className="w-20 rounded-lg bg-slate-50 py-3 px-3 text-center outline-none focus:border-blue focus-visible:shadow-none"

                          />

                          <input
                            type="text"
                            name="PhoneNumber"
                            value={formData.PhoneNumber}
                            placeholder={t("enterYourPhoneNumber")}
                            maxLength={15 - formData.CountryCode.replace("+", "").length}
                            onChange={(e) => {
                              const value = e.target.value;

                              if (/^\d*$/.test(value)) {
                                handleChange(e);
                              }
                            }}
                            className="flex-1 rounded-lg bg-slate-50 py-3 pl-6 pr-10 outline-none focus:border-blue focus-visible:shadow-none"
                            required={!isRegisteredUser}

                          />
                        </div>

                        <div className="min-h-[20px] mt-1">
                          {errors.PhoneNumber && (
                            <span className="text-red-500 text-xs">{errors.PhoneNumber}</span>
                          )}
                        </div>
                      </div>

                      {/* Referral ID input */}
                      <div>
                        {formData.ReferralName && (
                          <p className="mb-2 text-xs text-blue-600 font-semibold ">
                            Invited by {formData.ReferralName}
                          </p>
                        )}

                        <input
                          type="text"
                          name="ReferralId"
                          placeholder={t("referralIdOptional")}
                          value={formData.ReferralId}
                          onChange={(e) => {
                            const { value } = e.target;
                            if (/^\d*$/.test(value) && value.length <= 6) {
                              handleChange(e);
                            }
                          }}
                          maxLength={6}
                          className="w-full rounded-lg bg-slate-50 py-3 pl-6 pr-10 outline-none focus:border-blue focus-visible:shadow-none"
                        />

                        <div className="min-h-[20px] mt-1">
                          {errors.ReferralId && <span className="text-red-500 text-xs">{errors.ReferralId}</span>}
                        </div>
                      </div>

                    </>
                  )}

                  <div className="mb-6">
                    <input
                      type="email"
                      name="email"
                      placeholder={t("enterEmailToReceiveOtp")}
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full rounded-lg bg-slate-50 py-3 pl-6 pr-10 outline-none focus:border-blue focus-visible:shadow-none"
                      readOnly
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <div className="grid grid-cols-6 gap-3 w-full">
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          ref={ref => otpRefs.current[index] = ref}
                          id={`otp-${index}`}
                          type="text"
                          value={digit}
                          onChange={(e) => handleOtpChange(e, index)}
                          onKeyDown={(e) => handleKeyDown(e, index)}
                          maxLength={1}
                          className={`text-center text-base border ${otpError && otp.join('').length !== 6
                            ? 'border-red-500'
                            : 'border-whitesmoke'
                            } rounded-lg bg-ghostwhite h-12 w-full outline-none focus:border-blue focus-visible:shadow-none`} readOnly={!!autoFillOtp}
                        />
                      ))}
                    </div>
                    {!autoFillOtp && (
                      <p className="text-gray-500 text-sm mt-2">{t("otpSentMessage")}</p>
                    )}
                  </div>
                  {!autoFillOtp && !isRegisteredUser && (
                    <div className="mb-4 flex items-start">
                      <input
                        type="checkbox"
                        id="terms"
                        checked={agreedToTerms}
                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                        className="mt-1 mr-3 h-4 w-4 rounded cursor-pointer"
                      />
                      <label htmlFor="terms" className="text-gray-700 text-sm cursor-pointer leading-relaxed">
                        {t("readAndAgree")}{' '}
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            openModal("terms");
                          }}
                          className="text-blue-600 cursor-pointer hover:underline"
                        >
                          {t("termsOfService")}
                        </span>{' '}
                        {t("and")}{' '}
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            openModal("privacy");
                          }}
                          className="text-blue-600 cursor-pointer hover:underline"
                        >
                          {t("privacyPolicy")}{' '}
                        </span>

                      </label>
                    </div>
                  )}
                  {!autoFillOtp && (
                    <div className="mb-4 text-center">
                      <p className="text-gray-500 text-sm">
                        {t("didntReceiveCode")}&nbsp;
                        <button
                          type="button"
                          onClick={handleResendOtp}
                          className={`text-blue-600 font-medium hover:underline transition-all ${cooldown > 0 ? 'cursor-not-allowed text-gray-400' : ''}`}
                          disabled={loading || cooldown > 0}
                        >
                          {cooldown > 0 ? `${t("wait")} ${cooldown} ${t("seconds")}` : t("resend")}
                        </button>
                      </p>
                    </div>
                  )}


                  <div>
                    <button
                      type="submit"
                      className="w-full cursor-pointer bg-blue-600 shadow-lg shadow-blue-300/50 rounded-lg border border-primary p-3.5 font-semibold text-white transition-all duration-200 hover:bg-blue-500 hover:shadow-xl hover:shadow-blue-400/50 active:scale-[0.98] relative flex justify-center items-center disabled:opacity-60 disabled:cursor-not-allowed"
                      disabled={loading || attempts >= 5}
                    >
                      {loading ? (
                        <ReactLoading type="spin" color="#fff" height={24} width={24} />
                      ) : (
                        t("validateOTPBtn")
                      )}
                    </button>

                  </div>

                  {attempts >= 5 && (
                    <div className="mt-4 text-center">
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        className="w-full cursor-pointer bg-red-600 shadow-lg shadow-red-300/50 rounded-lg border border-primary p-3.5 font-semibold text-white transition-all duration-200 hover:bg-red-500 hover:shadow-xl hover:shadow-red-400/50 active:scale-[0.98]"
                      >
                        {t("resend")}
                      </button>
                    </div>
                  )}

                </form>
              </div>
            </div>
          </div>
        </div>
      </main>
      {modalType && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-md max-w-xl mx-auto relative">
            <h2 className="text-xl font-bold mb-4">
              {modalType === "privacy" ? t("privacyPolicy") : t("termsOfService")}
            </h2>


            <div className="max-h-96 overflow-y-auto text-sm text-gray-700">
              {modalType === "privacy" ? <PrivacyPolicyContent /> : <TermsOfServiceContent />}
            </div>
            <button
              onClick={closeModal}
              className="mt-4 bg-blue-600 text-white py-2 px-4 rounded"
            >
              {t("close")}
            </button>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>

  );
};

export default Register;
